import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';

export type PaginationPage = number | 'ellipsis-start' | 'ellipsis-end';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent implements OnChanges {
  @Input() totalItems: number = 0;
  @Input() itemsPerPage: number = 10;
  @Input() currentPage: number = 1;

  @Output() pageChange = new EventEmitter<number>();

  totalPages: number = 0;
  pages: PaginationPage[] = [];
  startItem: number = 0;
  endItem: number = 0;

  ngOnChanges(): void {
    this.totalPages = this.totalItems > 0 ? Math.ceil(this.totalItems / this.itemsPerPage) : 0;
    this.startItem = this.totalItems > 0 ? (this.currentPage - 1) * this.itemsPerPage + 1 : 0;
    this.endItem = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    this.pages = this.buildPages();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.pageChange.emit(page);
  }

  isEllipsis(page: PaginationPage): boolean {
    return page === 'ellipsis-start' || page === 'ellipsis-end';
  }

  pageAsNumber(page: PaginationPage): number {
    return page as number;
  }

  trackByPage(index: number, page: PaginationPage): string {
    return String(page) + '-' + index;
  }

  get hasPrev(): boolean {
    return this.currentPage > 1;
  }

  get hasNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  private buildPages(): PaginationPage[] {
    if (this.totalPages <= 7) {
      const result: PaginationPage[] = [];
      for (let i = 1; i <= this.totalPages; i++) {
        result.push(i);
      }
      return result;
    }

    const pages: PaginationPage[] = [];
    const delta = 2;
    const left = this.currentPage - delta;
    const right = this.currentPage + delta;

    pages.push(1);

    if (left > 2) {
      pages.push('ellipsis-start');
    }

    const rangeStart = Math.max(2, left);
    const rangeEnd = Math.min(this.totalPages - 1, right);
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (right < this.totalPages - 1) {
      pages.push('ellipsis-end');
    }

    pages.push(this.totalPages);
    return pages;
  }
}
